/**
 * Calendar events — standalone events that don't require a project.
 * The calendar page merges these with project due_dates + milestones
 * to build the full month view.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrgId } from '@/hooks/useOrgId';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useDemoStore } from '@/contexts/DemoStoreContext';

export interface CalendarEventRow {
  id: string;
  org_id: string;
  title: string;
  description: string | null;
  start_at: string;
  end_at: string;
  all_day: boolean;
  location: string | null;
  project_id: string | null;
  color: string | null;
  /** Public URL to a hero image rendered as a thumbnail on the event
   *  card. Studio uploads via the composer; bucket is `brand-assets`,
   *  path scoped to `${org_id}/calendar/...` per RLS rules. */
  image_url: string | null;
  /** Optional video-conference link (Zoom / Meet / Whereby / any).
   *  Renders as a "Join" button on the day-detail card and iOS event
   *  row; also pushed into ICS sync as a conferencing field. */
  meeting_url: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /** CAL-EDIT-1 — optional client this block is for. Independent of
   *  `project_id`; a block may carry both, either or neither. Optional
   *  in the type because the column arrives with
   *  20261103120000_calendar_event_client.sql and this ships first —
   *  see `withoutClientId` below. */
  client_id?: string | null;
}

/**
 * CAL-EDIT-1 — ship the composer before the migration lands.
 *
 * `client_id` is added by 20261103120000_calendar_event_client.sql,
 * which runs through Lovable, and that is not something this code can
 * wait on. PostgREST answers a write naming a column it does not know
 * with PGRST204 (or Postgres 42703), so the write is retried once
 * without it rather than failing in front of the studio. The block
 * saves; it just isn't linked to a client until the column exists.
 *
 * Deliberately narrow: ONE named column, and only on the way in. A
 * generic "strip whatever it complained about" retry is how a typo in
 * a column name becomes a silent data-loss bug.
 */
const missingClientColumn = (error: { code?: string; message?: string } | null) =>
  !!error &&
  (error.code === 'PGRST204' || error.code === '42703') &&
  /client_id/.test(error.message ?? '');

const withoutClientId = <T extends Record<string, unknown>>(payload: T) => {
  const { client_id: _dropped, ...rest } = payload;
  return rest;
};

export const useCalendarEvents = () => {
  const { data: orgId } = useOrgId();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useQuery({
    queryKey: ['calendar-events', orgId, isDemo, isDemo ? demoStore.version : 0],
    queryFn: async () => {
      if (isDemo) {
        return demoStore.calendarEvents.list() as unknown as CalendarEventRow[];
      }
      if (!orgId) return [] as CalendarEventRow[];
      const { data, error } = await supabase
        .from('calendar_events')
        .select('*')
        .eq('org_id', orgId)
        .order('start_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as CalendarEventRow[];
    },
    enabled: isDemo || !!orgId,
  });
};

export const useCreateCalendarEvent = () => {
  const queryClient = useQueryClient();
  const { data: orgId } = useOrgId();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useMutation({
    mutationFn: async (input: {
      title: string;
      description?: string | null;
      start_at: string;
      end_at: string;
      all_day?: boolean;
      location?: string | null;
      project_id?: string | null;
      client_id?: string | null;
      color?: string | null;
      image_url?: string | null;
      meeting_url?: string | null;
    }) => {
      if (isDemo) {
        const now = new Date().toISOString();
        const created = {
          id: `demo-ce-${crypto.randomUUID().slice(0, 8)}`,
          org_id: 'demo-org',
          all_day: false,
          description: null,
          location: null,
          project_id: null,
          color: null,
          image_url: null,
          meeting_url: null,
          created_by: 'demo-studio-user',
          created_at: now,
          updated_at: now,
          ...input,
        };
        return demoStore.calendarEvents.add(created) as unknown as CalendarEventRow;
      }
      if (!orgId) throw new Error('Your studio isn’t set up yet');
      const payload = { ...input, org_id: orgId };
      let { data, error } = await supabase
        .from('calendar_events')
        .insert(payload)
        .select()
        .single();
      if (missingClientColumn(error)) {
        ({ data, error } = await supabase
          .from('calendar_events')
          .insert(withoutClientId(payload))
          .select()
          .single());
      }
      if (error) throw error;
      return data as CalendarEventRow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    },
  });
};

export const useUpdateCalendarEvent = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<CalendarEventRow> & { id: string }) => {
      if (isDemo) {
        demoStore.calendarEvents.update(id, updates as any);
        return;
      }
      const patch = { ...updates, updated_at: new Date().toISOString() };
      let { error } = await supabase.from('calendar_events').update(patch).eq('id', id);
      if (missingClientColumn(error)) {
        ({ error } = await supabase
          .from('calendar_events')
          .update(withoutClientId(patch))
          .eq('id', id));
      }
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    },
  });
};

export const useDeleteCalendarEvent = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useMutation({
    mutationFn: async (id: string) => {
      if (isDemo) {
        demoStore.calendarEvents.remove(id);
        return;
      }
      const { error } = await supabase.from('calendar_events').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    },
  });
};
